import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import { Outlet, useMatches } from "@tanstack/react-router";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useTheme, type Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import { ThemeProvider } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import Box, { type BoxProps } from "@subly/s1/ui/atom/Box";
import Link from "@subly/s1/ui/atom/Link";
import Logo from "@subly/s1/ui/atom/Logo";
import AppBar from "@subly/s1/ui/base/AppBar";
import Accordion from "@subly/s1/ui/base/Accordion";
import Button from "@subly/s1/ui/base/Button";
import List from "@subly/s1/ui/base/List";
import Profile from "@subly/s1/ui/composed/Profile";
import QueryProvider from "@subly/s1/ui/composed/QueryProvider";

import core from "core";
import useNav from "core/hooks/useNav";

import Icon, { type IconName } from "ui/icon";

import { useLayout } from "features/layout/fabric";
import { useStart } from "features/start/fabric";

import base from "theme/base";
import dark from "theme/dark";
import light from "theme/light";

import type { ValidRoutes } from "router";
import Msal from "./Msal";


const Account = ({ sidebar }: { sidebar?: boolean }) => {
	const layout = useLayout();
	const auth0 = useAuth0();
	const theme = useTheme();
	const lite = useMediaQuery(t => t.breakpoints.down("md"));


	const user = auth0.user;
	const name = user?.name;

	const style = {
		root: { justifyContent: "center", textAlign: "right" },
		balance: { alignItems: "center", justifyContent: "flex-end" }
	};

	return lite
		? <Button.Icon icon="menu" size={22} color="primary" onClick={layout.openSidebar} />
		: (
			<Profile name={name} onClick={sidebar ? layout.openSidebar : undefined} textColor={theme.palette.text.primary}>
				<Box.Column gap={0.5} sx={style.root}>
					<Typography variant="body4">{name}</Typography>
					<Typography variant="body4">{user?.email}</Typography>
				</Box.Column>
			</Profile>
		);
};

const Header = ({ sidebar }: { sidebar?: boolean }) => {
	const theme = useTheme();

	const style = {
		root: {
			width: "100%",
			background: "rgba(255, 255, 255, 0.11)",
			backdropFilter: "blur(4px)",
			borderBottom: `1px solid ${theme.palette.divider}`,
			gridArea: "h"
		},
		toolbar: { height: theme.size.header.height.in },
		link: { display: "flex" }
	};

	return (
		<AppBar color="transparent" sx={style.root}>
			<AppBar.Toolbar sx={style.toolbar}>
				<Link to="/" sx={style.link}>
					<Logo size={118} color={theme.palette.logo.black} />
				</Link>
				<Account sidebar={sidebar} />
			</AppBar.Toolbar>
		</AppBar>
	);
};

const Sidebar = ({ hidden = false }) => {
	const layout = useLayout();
	const theme = useTheme();
	const start = useStart();
	const nav = useNav();
	const isTablet = useMediaQuery(t => t.breakpoints.down("md"));

	const name = start.user?.nickname || start.user?.name;

	const permanent = !hidden && !isTablet;
	const pt = theme.size.header.height.out;

	const style = {
		root: {
			gridArea: "s",
			height: { xs: "100%", md: `calc(100% - ${pt}px)` },
			top: { md: `${pt}px` },
			borderRight: 0,
			minWidth: `${theme.size.sidebar.width}px`,
			pt: { xs: 1, lg: 0 },
			background: {
				xs: theme.palette.background.paper,
				md: hidden ? theme.palette.background.paper : "transparent"
			}
		},
		list: { position: "relative", height: "100%", pl: 3 },
		display: { p: theme.spacing(2, 4), gap: theme.spacing(1.75) },
		menu: { mb: 1.25 },
		mark: {
			position: "absolute",
			bottom: 0, left: "50%",
			transform: "translateX(-50%)"
		} as React.CSSProperties,
		divider: { my: theme.spacing(2) },
		button: { justifyContent: "flex-start" }
	};

	type Section = {
		title?: string;
		path?: ValidRoutes;
		icon?: IconName;
		divider?: boolean;
		click?: () => void;
		display?: string;
	};

	const sections: Section[] = [];

	const go = (to: ValidRoutes) => {
		layout.closeSidebar();
		nav.go({ to });
	};

	return (
		<Drawer
			open={permanent ? true : layout.sidebar}
			anchor={permanent ? "left" : "right"}
			variant={permanent ? "permanent" : "temporary"}
			onClose={layout.closeSidebar}
			slotProps={{ paper: { sx: style.root } }}
		>
			{!permanent && <Profile.Full size={40} name={name} sx={style.display} />}
			<List sx={style.list}>
				<List.Item dense sx={style.menu}>
					<Typography variant="h6">Menu</Typography>
				</List.Item>
				{sections?.map((sec, i) => {
					const active = nav.location.pathname === sec.path;
					if (sec.divider) return <Divider variant="middle" sx={style.divider} key={i} />;
					return (
						<List.Item dense key={i}>
							<Button
								startIcon={sec.icon && <Icon name={sec.icon} size={22} />}
								variant={active ? "contained" : "text"}
								color={active ? "secondary" : "primary"}
								onClick={() => sec.click ? sec.click() : sec.path && go(sec.path)}
								fullWidth
								sx={{ ...style.button, display: sec.display && sec.display }}
							>
								{sec.title}
							</Button>
						</List.Item>
					);
				})}
				<Logo.Mark size={210} color={theme.palette.logo.lite} sx={style.mark} />
			</List>
		</Drawer>
	);
};

const Body = ({ children, ...props }: BoxProps) => {
	const style = { gridArea: "b", overflow: "hidden" };
	return <Box {...props} sx={{ ...style, ...props.sx }}>{children}</Box>;
};

const Footer = ({ lite }: { lite?: boolean }) => {
	const [open, setOpen] = useState<number | null>(null);
	const theme = useTheme();
	const isMobile = useMediaQuery(t => t.breakpoints.down("sm"));
	const start = useStart();
	const contact = start.settings?.contact;
	const email = contact.email;
	const social = contact.social;

	const style = {
		root: {
			gridArea: "f",
			alignItems: "center",
			justifyContent: "center",
			width: "100%",
			padding: theme.spacing(2.5, 2),
			mx: "auto"
		},
		main: {
			width: "100%",
			alignItems: "center",
			justifyContent: "space-between",
			padding: {
				xs: theme.spacing(3),
				sm: theme.spacing(6.25, 7.5, 5, 7.5)
			},
			borderRadius: { xs: "16px", sm: "38px" },
			background: "rgba(0, 0, 0, 0.15)",
			boxShadow: "0px 0px 50px 0px rgba(107, 111, 181, 0.24)"
		},
		inner: {
			width: "100%",
			flexDirection: { xs: "column", sm: "row" },
			flexWrap: { xs: "nowrap", sm: "wrap" },
			alignItems: "flex-start",
			justifyContent: "space-between"
		},
		options: {
			flexWrap: { xs: "nowrap", sm: "wrap" },
			flexDirection: { xs: "column", sm: "row" },
			gap: { xs: 0, sm: 4, md: 12 },
			width: { xs: "100%", sm: "auto" },
			mx: { xs: 0, sm: "auto" }
		},
		accordion: {
			backgroundImage: "none",
			boxShadow: "none",
			"&:before": {
				backgroundColor: "none"
			}
		},
		details: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1.5)
		},
		platforms: {
			width: "100%",
			justifyContent: { xs: "center", sm: "flex-start" }
		},
		contact: {
			mt: theme.spacing(1)
		},
		lite: {
			background: theme.palette.background.paper,
			position: "fixed", bottom: 0, left: 0, right: 0,
			textAlign: "center",
			p: theme.spacing(1)
		}
	};

	type Option = {
		title: string;
		items: { label: string; to?: ValidRoutes; tab?: boolean }[];
	};

	const options: Option[] = [
		{
			title: "Information",
			items: [
				{ label: "Media Partners" },
				{ label: "Customers" },
				{ label: "FAQ" }
			]
		},
		{
			title: "Company",
			items: [
				{ label: "Careers" },
				{ label: "Investors" },
				{ label: "Company Info" }
			]
		},
		{
			title: "Help",
			items: [
				{ label: "Privacy", tab: true },
				{ label: "Legal", tab: true },
				{ label: "Terms", tab: true },
				{ label: "SMS Terms", tab: true }
			]
		}
	];

	if (lite) return (
		<Typography component="span" variant="body4" color="text.secondary" sx={style.lite}>
			By continuing, you agree to or <Link tab color="tertiary.main">Terms & Conditions</Link>.
		</Typography>
	);
	return (
		<Box.Column maxWidth="lg" gap={3.5} sx={style.root}>
			<Box.Column gap={1.25} sx={style.main}>
				<Box gap={2} sx={style.inner}>
					<Logo size={215} />
					<Box sx={style.options}>
						{options.map((o, k) => (
							<Accordion disableGutters expanded={!isMobile || open === k} onChange={() => setOpen(s => s === k ? null : k)} slotProps={{ root: { sx: style.accordion } }} key={k}>
								<Accordion.Summary icon={!isMobile ? undefined : open === k ? "minus" : "plus" } variant="reverse">
									{o.title}
								</Accordion.Summary>
								<Accordion.Details sx={style.details}>
									{o.items.map((item, i) => <Link tab={item.tab} variant="body2" to={item.to} key={i}>{item.label}</Link>)}
								</Accordion.Details>
							</Accordion>
						))}
					</Box>
				</Box>
				<Box gap={2} sx={style.platforms}>
					<Button.Icon icon="facebook" size={25} target="_blank" href={social.facebook} />
					<Button.Icon icon="instagram" size={25} target="_blank" href={social.instagram} />
					<Button.Icon icon="twitter" size={25} target="_blank" href={social.x} />
					<Button.Icon icon="linkedin" size={25} target="_blank" href={social.linkedin} />
				</Box>
				<Typography variant="body1">
					<b>Questions?</b> <Link href={`mailto:${email.info}`} color="text.secondary" sx={style.contact}>Contact us</Link>
				</Typography>
			</Box.Column>
			<Typography variant="body4" color="text.secondary">© {new Date().getFullYear()} Subly Media Inc.</Typography>
		</Box.Column>
	);
};

Layout.Light = () => {
	const theme = core.theme.merge(base, light);
	const size = theme.size;

	const style = {
		root: {
			display: "grid",
			gridTemplateRows: `${size.header.height.in}px 1fr`,
			backgroundColor: "background.default",
			minHeight: "100vh",
			gridTemplateAreas: `
				"h"
				"b"
			`
		}
	};

	return (
		<Layout theme={theme}>
			<Box sx={style.root}>
				<Header />
				<Body>
					<Outlet />
				</Body>
			</Box>
		</Layout>
	);
};

Layout.Auth = () => {
	const theme = core.theme.merge(base, light);

	const style = {
		root: {
			display: "grid",
			backgroundColor: "secondary.dark",
			minHeight: "100vh",
			gridTemplateRows: "1fr",
			gridTemplateAreas: `
			"b"
			`
		}
	} as SxProps;
	return (
		<Layout theme={theme}>
			<Box sx={style.root}>
				<Body>
					<Outlet />
				</Body>
			</Box>
		</Layout>
	);
};

Layout.Dark = () => {
	const theme = core.theme.merge(base, dark);
	const matches = useMatches();

	const match = matches[matches.length - 1];
	const size = theme.size;

	const data = match?.staticData;
	const lite = data?.lite;
	const footer = data?.footer;

	const style = {
		root: {
			display: "grid",
			gridTemplateRows: lite ? "1fr" : `${size.header.height.in}px minmax(calc(100vh - ${size.header.height.out}px - ${size.footer.height}px), 1fr) auto`,
			background: theme.palette.background.paper,
			minHeight: "100vh",
			alignItems: lite ? "center" : undefined,
			gridTemplateColumns: "100%",
			gridTemplateAreas: lite
				? `
					"b"
				`

				: `
					"h"
					"b"
					"f"
				`
		}
	};

	return (
		<Layout theme={theme}>
			<Box sx={style.root}>
				{!lite && (
					<>
						<Header sidebar />
						<Sidebar hidden />
					</>
				)}
				<Body>
					<Outlet />
				</Body>
				{footer && <Footer lite={lite} />}
			</Box>
		</Layout>
	);
};

const Content = ({ children }: { children: React.ReactNode }) => (
	<>
		{children}
		<CssBaseline />
		<ReactQueryDevtools buttonPosition="bottom-right" />
	</>
);

export default function Layout({ theme, children }: { theme: Theme, children: React.ReactNode }) {
	const hideMsal = import.meta.env.MODE !== "staging";
	return (
		<QueryProvider>
			<ThemeProvider theme={theme}>
				{hideMsal
					? <Content>{children}</Content>
					: (
						<Msal>
							<Content>
								{children}
							</Content>
						</Msal>
					)
				}
			</ThemeProvider>
		</QueryProvider>
	);
}